import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { IoArrowForward, IoShieldCheckmarkOutline } from "react-icons/io5";
import {
  FaBoxesStacked,
  FaPlane,
  FaPrint,
  FaTruck,
} from "react-icons/fa6";
import { MdOutlineWarehouse } from "react-icons/md";

import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";
import banner6 from "../assets/banner6.jpg";
import banner7 from "../assets/banner7.jpg";
import print from "../assets/print.jpg";
import supplies from "../assets/computer.jpg";
import bg from "../assets/banner7.jpg";
import ardhi from "../assets/clients/ARDHI.png";
import bonjour from "../assets/clients/bonjour.png";
import bot from "../assets/clients/BOT.png";
import fcs from "../assets/clients/FCS.png";
import icap from "../assets/clients/icap.png";
import jw from "../assets/clients/JW.jpg";
import marine from "../assets/clients/MARINE.jpg";
import mzumbe from "../assets/clients/MZUMBE.jpg";
import pact from "../assets/clients/PACT.png";
import { IoMdArrowForward } from "react-icons/io";

const EMPTY_REVEAL_OPTIONS = {};

/* ------------------------------------------------------------------ */
/* Tiny scroll-reveal hook (replaces AOS / generic fade-up)           */
/* ------------------------------------------------------------------ */
function useReveal(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const revealOptions = options ?? EMPTY_REVEAL_OPTIONS;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px", ...revealOptions }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealOptions]);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function View() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  const pillars = useMemo(
    () => [
      {
        kicker: "01  Logistics",
        title: "Global Logistics & Freight Forwarding",
        mission:
          "Tailored logistics that keep cargo moving  seamless operations, regional connectivity, and no surprises.",
        vision:
          "A pioneering force across East Africa, shaping how the region ships, stores and distributes.",
        values: ["Teamwork", "Integrity", "Professionalism", "Innovation"],
        icon: <FaTruck />,
        image: banner4,
      },
      {
        kicker: "02  Supplies",
        title: "Printing & Office Supplies",
        mission:
          "High-quality print and stationery built around each client's real workflow  sustainable, precise, on-brand.",
        vision:
          "The country's first call for print and supply  setting the standard for craft and service.",
        values: ["Quality", "Innovation", "Customer-Centric", "Sustainability"],
        icon: <FaPrint />,
        image: supplies,
      },
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        title: "Air & Ocean Cargo",
        text: "Fast international transport backed by dependable maritime and aviation coordination.",
        icon: <FaPlane />,
        image: banner4,
        meta: "International",
      },
      {
        title: "Customs Clearance",
        text: "Compliance-first processing that removes friction at every border.",
        icon: <IoShieldCheckmarkOutline />,
        image: banner5,
        meta: "Compliance",
      },
      {
        title: "Cross-Border Trucking",
        text: "Regional road freight engineered for the terrain, the load and the deadline.",
        icon: <FaTruck />,
        image: banner6,
        meta: "Regional",
      },
      {
        title: "Warehousing & Handling",
        text: "Secure staging, cargo handling and transit support before final distribution.",
        icon: <MdOutlineWarehouse />,
        image: banner7,
        meta: "Storage",
      },
      {
        title: "Large Format Printing",
        text: "Industrial output for banners, billboards, canvas, blueprints and display systems.",
        icon: <FaPrint />,
        image: print,
        meta: "Print",
      },
      {
        title: "Stationery & Office Supplies",
        text: "Bulk delivery of the essentials that keep offices and classrooms running.",
        icon: <FaBoxesStacked />,
        image: supplies,
        meta: "Supplies",
      },
    ],
    []
  );

  const clients = useMemo(
    () => [
      { name: "ARDHI", logo: ardhi },
      { name: "Bonjour", logo: bonjour },
      { name: "Bank of Tanzania", logo: bot },
      { name: "FCS", logo: fcs },
      { name: "ICAP", logo: icap },
      { name: "JW", logo: jw },
      { name: "Marine", logo: marine },
      { name: "Mzumbe", logo: mzumbe },
      { name: "PACT", logo: pact },
    ],
    []
  );

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <section className="corp-page">
      {/* ---------- MODERN & CLASSIC HERO ---------- */}
      <header className="modern-hero">
        <style>{`
          .modern-hero {
            position: relative;
            height: calc(100vh - var(--topBarHeight)); 
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            background-color: #0f172a;
            font-family: system-ui, -apple-system, sans-serif;
          }
          .modern-hero-bg {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
          }
          .modern-hero-slide {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            background-size: cover;
            background-position: center;
            opacity: 0;
            animation: fadeZoom 16s infinite ease-in-out;
          }
          @keyframes fadeZoom {
            0% { opacity: 0; transform: scale(1); }
            10% { opacity: 1; transform: scale(1.02); }
            25% { opacity: 1; transform: scale(1.05); }
            35% { opacity: 0; transform: scale(1.08); }
            100% { opacity: 0; transform: scale(1.1); }
          }
          .modern-hero-slide:nth-child(1) { animation-delay: 0s; }
          .modern-hero-slide:nth-child(2) { animation-delay: 4s; }
          .modern-hero-slide:nth-child(3) { animation-delay: 8s; }
          .modern-hero-slide:nth-child(4) { animation-delay: 12s; }

          .modern-hero-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.9) 100%);
            z-index: 1;
          }
          .modern-hero-content {
            position: relative;
            text-align: center;
            width: 100%;
            max-width: 900px;
            padding: 0 24px;
            margin-top: -8vh; 
          }
          .modern-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(8px);
            padding: 8px 20px;
            border-radius: 50px;
            color: #e2e8f0;
            font-size: 0.8rem;
            font-weight: 600;
            letter-spacing: 0.15em;
            text-transform: uppercase;
            margin-bottom: 32px;
          }
          .modern-eyebrow-dot {
            width: 8px;
            height: 8px;
            background-color: #3b82f6;
            border-radius: 50%;
            box-shadow: 0 0 10px rgba(59, 130, 246, 0.8);
          }
          .modern-title {
            font-size: clamp(3rem, 8vw, 6rem);
            font-weight: 800;
            line-height: 1.05;
            color: #ffffff;
            margin: 0 0 20px 0;
            letter-spacing: -0.03em;
            text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          }
          .modern-subtitle {
            font-size: clamp(1.125rem, 3vw, 1.35rem);
            font-weight: 400;
            color: #e8eaee;
            max-width: 650px;
            margin: 0 auto 40px auto;
            line-height: 1.6;
          }
          .modern-subtitle-highlight {
            color: #60a5fa;
            font-weight: 500;
          }
          .modern-cta-group {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 16px;
            flex-wrap: wrap;
          }
          .modern-btn {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 16px 32px;
            border-radius: 8px;
            font-size: 1.05rem;
            font-weight: 600;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
            border: none;
          }
          .modern-btn-primary {
            background-color: #2563eb;
            color: white;
            box-shadow: 0 4px 14px 0 rgba(37, 99, 235, 0.39);
          }
          .modern-btn-primary:hover {
            background-color: #1d4ed8;
            transform: translateY(-3px);
            box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
          }
          .modern-btn-outline {
            background-color: transparent;
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(5px);
          }
          .modern-btn-outline:hover {
            background-color: rgba(255, 255, 255, 0.1);
            border-color: rgba(255, 255, 255, 0.8);
            transform: translateY(-3px);
          }
          /* --- Marquee --- */
          .modern-marquee {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            background: rgba(15, 23, 42, 0.5);
            backdrop-filter: blur(12px);
            border-top: 1px solid rgba(255, 255, 255, 0.08);
            display: flex;
            overflow: hidden;
            padding: 20px 0;
          }
          .modern-marquee-track {
            display: flex;
            white-space: nowrap;
            animation: marqueeScroll 25s linear infinite;
          }
          .modern-marquee-item {
            display: inline-flex;
            align-items: center;
            color: #cbd5e1;
            font-size: 0.9rem;
            font-weight: 500;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            margin: 0 32px;
          }
          .modern-marquee-dot {
            width: 5px;
            height: 5px;
            background-color: #60a5fa;
            border-radius: 50%;
            margin-right: 16px;
          }
          @keyframes marqueeScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @media (max-width: 768px) {
            .modern-cta-group { flex-direction: column; width: 100%; }
            .modern-btn { width: 100%; justify-content: center; }
          }
        `}</style>

        <div className="modern-hero-bg">
          {[banner4, banner5, banner6, banner7].map((image, i) => (
            <div
              key={image}
              className="modern-hero-slide"
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
          <div className="modern-hero-overlay" />
        </div>

        <div className="modern-hero-content">

          <Reveal delay={100}>
            <h1 className="modern-title">
              PACLESE
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="modern-subtitle">
              <span className="modern-subtitle-highlight font-bold">Cargo Freight & Forwarding Company Limited</span> <br/>
              Tailored logistics, supplies, and regional connectivity across East Africa.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="modern-cta-group">
              <button
                className="modern-btn modern-btn-primary"
                onClick={() => appContext.navTo({ item: "contact_us" })}
              >
                Get a shipping quote
                <IoArrowForward />
              </button>
              <button
                className="modern-btn modern-btn-outline"
                onClick={() => appContext.navTo({ item: "services" })}
              >
                Browse supplies
              </button>
            </div>
          </Reveal>
        </div>

        <div className="modern-marquee" aria-hidden>
          <div className="modern-marquee-track">
            {Array.from({ length: 2 }).map((_, loop) => (
              <div key={loop} style={{ display: 'flex' }}>
                {[
                  "Freight Forwarding",
                  "Air & Ocean Cargo",
                  "Customs Clearance",
                  "Cross-Border Trucking",
                  "Warehousing",
                  "Large Format Print",
                  "Office & Tech Supplies",
                ].map((t) => (
                  <span key={`${loop}-${t}`} className="modern-marquee-item">
                    <span className="modern-marquee-dot" />
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ---------- WHO WE ARE ---------- */}
      <section className="corp-section corp-about">
        <div className="container corp-about-grid">
          <div className="corp-about-intro">
            <Reveal>
              <span className="corp-section-kicker">/ Who we are</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="corp-section-title text-light font-bold">
                Paclese Freight & Forwarding
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="corp-section-body">
                Established in 2017, registered in 2021, and incorporated in
                2022  PACLESE CARGO has grown from cross-border forwarding into
                a single point of contact for logistics, print, and the everyday
                supplies that keep modern organisations running.
              </p>
            </Reveal>
          </div>

          <figure className="corp-about-image" data-reveal>
            <img src={bg} alt="Paclese cargo operations" loading="lazy" />
            <figcaption>
              <span>Since 2017</span>
            </figcaption>
          </figure>

          <div className="corp-about-stats">
            {[
              { n: "2017", l: "Founded" },
              { n: "02", l: "Service divisions" },
              { n: "EA", l: "Regional reach" },
              { n: "24/7", l: "Operations support" },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 80} className="corp-stat">
                <span className="corp-stat-n">{s.n}</span>
                <span className="corp-stat-l">{s.l}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- PILLARS ---------- */}
      <section className="corp-section corp-pillars">
        <div className="container">
          <Reveal delay={80}>
            <h2 className="corp-section-title corp-section-title--wide text-primary font-bold">
              Two divisions.
              <br />
              One operating standard.
            </h2>
          </Reveal>
        </div>

        <style>{`
          .corp-pillars .corp-pillar-meta dd {
          }
          .corp-pillars .corp-principles-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px 14px;
          }
          .corp-pillars .corp-principles-list li {
            position: relative;
            padding-left: 14px;
          }
          .corp-pillars .corp-principles-list li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0.55em;
            width: 6px;
            height: 1px;
            background: var(--corp-accent);
          }
          .corp-pillar--right .corp-principles-list {
            justify-items: end;
          }
          .corp-pillar--right .corp-principles-list li {
            padding-left: 0;
            padding-right: 14px;
            text-align: right;
          }
          .corp-pillar--right .corp-principles-list li::before {
            left: auto;
            right: 0;
          }
          @media (max-width: 768px) {
            .corp-pillars .corp-principles-list {
              grid-template-columns: 1fr;
            }
            .corp-pillar--right .corp-principles-list {
              justify-items: start;
            }
            .corp-pillar--right .corp-principles-list li {
              padding-left: 14px;
              padding-right: 0;
              text-align: left;
            }
            .corp-pillar--right .corp-principles-list li::before {
              left: 0;
              right: auto;
            }
          }
        `}</style>

        <div className="corp-pillars-list">
          {pillars.map((p, i) => (
            <article
              key={p.title}
              className={`corp-pillar corp-pillar--${i === 0 ? "left" : "right"} px-3`}
            >
              <div className="corp-pillar-media">
                <img src={p.image} alt={p.title} loading="lazy" />
                <div className="corp-pillar-index">{p.kicker}</div>
              </div>

              <div className="corp-pillar-body">
                <Reveal>
                  <h3 className="font-bold">{p.title}</h3>
                </Reveal>
                <Reveal delay={80}>
                  <p className="corp-lead">{p.mission}</p>
                </Reveal>

                <dl className="corp-pillar-meta">

                  <div>
                    <dt>Principles</dt>
                    <dd>
                      <ul className="corp-principles-list">
                        {p.values.map((v) => (
                          <li key={v}>{v}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>

                  <div>
                    <dt>Vision</dt>
                    <dd>{p.vision}</dd>
                  </div>
                  
                </dl>

                <Reveal delay={160}>
                  <button
                    className="corp-link"
                    onClick={() => appContext.navTo({ item: "services" })}
                  >
                    See how we deliver this
                    <IoArrowForward />
                  </button>
                </Reveal>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ---------- CLIENTS ---------- */}
      <section className="corp-section corp-clients">
        <style>{`
          .corp-clients {
            background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
          }
          .corp-clients .corp-section-title {
            margin-bottom: 14px;
          }
          .corp-clients-intro {
            max-width: 720px;
            margin-bottom: 28px;
          }
          .client-slider {
            position: relative;
            border: 1px solid rgba(37, 99, 235, 0.12);
            border-radius: 28px;
            background: rgba(255, 255, 255, 0.88);
            box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06);
            overflow: hidden;
            padding: 26px 0;
          }
          .client-slider::before,
          .client-slider::after {
            content: "";
            position: absolute;
            top: 0;
            bottom: 0;
            width: 72px;
            z-index: 2;
            pointer-events: none;
          }
          .client-slider::before {
            left: 0;
            background: linear-gradient(90deg, #f8fbff 0%, rgba(248, 251, 255, 0) 100%);
          }
          .client-slider::after {
            right: 0;
            background: linear-gradient(270deg, #f8fbff 0%, rgba(248, 251, 255, 0) 100%);
          }
          .client-slider-track {
            display: flex;
            width: max-content;
            gap: 18px;
            padding: 0 18px;
            animation: clientTrack 28s linear infinite;
            will-change: transform;
          }
          .client-slider:hover .client-slider-track {
            animation-play-state: paused;
          }
          .client-slide {
            flex: 0 0 180px;
            height: 110px;
            border-radius: 20px;
            border: 1px solid rgba(15, 23, 42, 0.08);
            background: #ffffff;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
            display: grid;
            place-items: center;
            padding: 18px;
            transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
          }
          .client-slide:hover {
            transform: translateY(-2px);
            border-color: rgba(37, 99, 235, 0.2);
            box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
          }
          .client-slide img {
            max-width: 100%;
            max-height: 56px;
            object-fit: contain;
            transition: filter 220ms ease, opacity 220ms ease, transform 220ms ease;
          }
          .client-slide:hover img {
            opacity: 1;
            transform: scale(1.03);
          }
          @keyframes clientTrack {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @media (max-width: 768px) {
            .client-slider {
              border-radius: 22px;
              padding: 18px 0;
            }
            .client-slider::before,
            .client-slider::after {
              width: 42px;
            }
            .client-slide {
              flex-basis: 148px;
              height: 96px;
            }
          }
        `}</style>

        <div className="container">
          <div className="corp-clients-intro">
            <Reveal>
              <span className="corp-section-kicker text-muted">/ Our Clients</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="corp-section-title text-primary font-bold">
                Trusted by organizations across sectors
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="corp-section-body text-muted font-semi-bold">
                We support public institutions, development partners, and
                growing teams with logistics and supplies that are delivered
                with care.
              </p>
            </Reveal>
          </div>

          <div className="client-slider" aria-label="Our clients slider">
            <div className="client-slider-track">
              {[...clients, ...clients].map((client, index) => (
                <div className="client-slide" key={`${client.name}-${index}`}>
                  <img src={client.logo} alt={client.name} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ---------- CTA BAND ---------- */}
      <section className="corp-cta-band">
        <img src={banner5} alt="" className="corp-cta-band-bg" />
        <div className="corp-cta-band-overlay" />
        <div className="container corp-cta-band-inner">
          <Reveal>
            <span className="corp-section-kicker corp-section-kicker--inv">
              / Let's move things forward
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h2>
              Need a quote or a <b className="text-primary font-bold">supply partner</b>?
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p>
              We'll put together a shipping plan, a supply package, or a mixed
              corporate solution  sized to your team.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <button
              className="corp-btn corp-btn-primary corp-btn-primary--inv"
              onClick={() => appContext.navTo({ item: "contact_us" })}
            >
              Start a conversation
              <IoArrowForward />
            </button>
          </Reveal>
        </div>
      </section>


      {/* ---------- SERVICES (editorial alternating rows) ---------- */}
      <section className="corp-section corp-services">
        <div className="container">
          <Reveal>
            <span className="corp-section-kicker">/ Services</span>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="corp-section-title text-primary font-bold">
              A portfolio of Our Services
            </h2>
          </Reveal>
        </div>

        <ul className="corp-services-list">
          {services.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <li
                key={s.title}
                className={`corp-service-row ${reversed ? "is-reversed" : ""}`}
              >
                <figure className="corp-service-media">
                  <img src={s.image} alt={s.title} loading="lazy" />
                  <span className="corp-service-icon">{s.icon}</span>
                  <span className="corp-service-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </figure>

                <div className="corp-service-body">
                  <Reveal>
                    <span className="corp-meta">{s.meta}</span>
                  </Reveal>
                  <Reveal delay={80}>
                    <h3 className="font-bold">{s.title}</h3>
                  </Reveal>
                  <Reveal delay={140}>
                    <p>{s.text}</p>
                  </Reveal>
                  <Reveal delay={200}>
                    <button
                      className="corp-link"
                      onClick={() => appContext.navTo({ item: "contact_us" })}
                    >
                      Request this service
                      <IoArrowForward />
                    </button>
                  </Reveal>
                </div>
              </li>
            );
          })}
        </ul>
        
        <div className="text-center mt-5">
          <button className="btn btn-outline-light p-3 btn-lg" onClick={() => appContext.navTo({ item: "services" })}>
            View All Services <IoMdArrowForward size={32} />
          </button>
        </div>
        
      </section>

      {/* ---------- CTA BAND ---------- */}
      <section className="corp-cta-band">
        <img src={banner7} alt="" className="corp-cta-band-bg" />
        <div className="corp-cta-band-overlay" />
        <div className="container corp-cta-band-inner">
          <Reveal>
            <span className="corp-section-kicker corp-section-kicker--inv">
              / Let's move things forward
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h2>
              Need a quote or a <b className="text-primary font-bold">supply partner</b>?
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p>
              We'll put together a shipping plan, a supply package, or a mixed
              corporate solution  sized to your team.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <button
              className="corp-btn corp-btn-primary corp-btn-primary--inv"
              onClick={() => appContext.navTo({ item: "contact_us" })}
            >
              Start a conversation
              <IoArrowForward />
            </button>
          </Reveal>
        </div>
      </section>

    </section>
  );
}
